package gr.tech.site.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.article.ARTICLE_ENTRIES
import gr.tech.site.blog.BLOG_ENTRIES
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.PartnersSectionAnimated
import gr.tech.site.components.states.FaqAccordion
import gr.tech.site.components.states.FaqItem
import gr.tech.site.data.NEXT_EVENTS
import gr.tech.site.data.NextEventCard
import gr.tech.site.data.PODCAST_LIST
import gr.tech.site.styles.*
import gr.tech.site.styles.landings.LandingAttributionStyle
import gr.tech.site.styles.landings.LandingBodyStyle
import gr.tech.site.styles.landings.LandingDarkSectionHeadingStyle
import gr.tech.site.styles.landings.LandingQuoteMarkStyle
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.text.Typography.quote

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Energy Rebates & Incentives for Growers and Commercial Facilities",
        canonicalUrl = "https://greenrebates.com/"
    ))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {

    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize().role("main")
            .toAttrs()
    )
    {

        // Hero Split
        Section(
            attrs = HeroSplitStyle.toModifier()
                .toAttrs()
        )
        {

            // Left Column — Photo background with centered iFrame
            Div(attrs = HeroPhotoCellStyle.toModifier().toAttrs()) {
                Img(
                    src = "greenhouse-hero.webp",
                    alt = "Commercial greenhouse with LED grow lighting",
                    attrs = Modifier
                        .position(Position.Absolute)
                        .fillMaxSize()
                        .top(0.px).left(0.px)
                        .toAttrs {
                            attr("fetchpriority", "high")
                            style { objectFit(ObjectFit.Cover) }
                        }
                )
                Div(
                    attrs = HeroOverlayStyle.toModifier()
                        .classNames("hero-overlay")
                        .position(Position.Absolute)
                        .fillMaxSize()
                        .top(0.px).left(0.px)
                        .toAttrs()
                )
                Div(
                    attrs = HeroIframeContainerStyle.toModifier()
                        .classNames("hero-iframe-wrapper")
                        .position(Position.Relative)
                        .zIndex(2)
                        .toAttrs()
                ) {
                    Iframe(
                        attrs = Modifier
                            .fillMaxSize()
                            .toAttrs {
//                                attr("loading", "lazy")
                                attr("src", "https://www.youtube-nocookie.com/embed/866Mh94VtBc?si=J_lgbx_SAuxgCppd&autoplay=1&mute=1&enablejsapi=1")
                                attr("title", "YouTube video player")
                                attr("frameborder", "0")
                                attr("referrerpolicy", "strict-origin-when-cross-origin")
                                attr("allowfullscreen", "true")
                                attr("allow", "autoplay; encrypted-media")
                            }
                    )
                }
            }

            // Right Column — Copy
            Div(attrs = HeroCopyCellStyle.toModifier().toAttrs()) {

                // Eyebrow
                Div(
                    attrs = HeroEyebrowStyle.toModifier()
                        .classNames("d-flex", "align-items-center")
                        .color(Theme.MediumGreen.rgb)
                        .margin(bottom = 24.px)
                        .toAttrs()
                ) {
                    Div(
                        attrs = Modifier
                            .width(32.px)
                            .height(2.px)
                            .backgroundColor(Theme.MediumGreen.rgb)
                            .toAttrs()
                    )
                    Text("Energy Incentive Management")
                }

                // H1
                H1(
                    attrs = HeroH1Style.toModifier()
                        .color(Theme.DarkGreen.rgb)
                        .classNames("fw-bold")
                        .margin(bottom = 24.px)
                        .toAttrs()
                ) {
                    Text("Maximize Your"); Br()
                    Em(
                        attrs = Modifier
                            .color(Theme.AccentGreen.rgb)
                            .toAttrs()
                    ) { Text("Energy Rebates & Incentives") }
                }

                // Body
                P(
                    attrs = HeroBodyStyle.toModifier()
                        .color(Theme.DarkGreen.rgb)
                        .margin(bottom = 40.px)
                        .toAttrs()
                ) {
                    Text("Green Rebates is the premier energy incentive firm for the horticulture industry. We bridge the gap between commercial growers and utility providers by securing millions in cash incentives for efficient LED lighting, advanced HVAC, and industrial dehumidification upgrades.")
                }

                // CTAs
                Div(
                    attrs = HeroCtaRowStyle.toModifier()
                        .classNames("d-flex", "flex-wrap", "flex-row")
                        .toAttrs()
                ) {
                    Link(
                        path = "/contact-us",
                        text = "GET A FREE ESTIMATE",
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = ButtonHoverDarkGreen.toModifier()
                            .classNames("fs-6", "px-4", "py-3", "shadow")
                            .role("button").attrsModifier {
                                attr("aria-label", "Get a free rebate estimate")
                            },
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                    Link(
                        path = "/our-services",
                        text = "SEE HOW IT WORKS",
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = ButtonHoverDarkGreen.toModifier()
                            .classNames("fs-6", "px-4", "py-3")
                            .role("button").attrsModifier {
                                attr("aria-label", "See how Green Rebates works")
                            },
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }

                // Trust line
                Div(
                    attrs = HeroTrustLineStyle.toModifier()
                        .classNames("mt-3", "pt-3")
                        .borderTop(1.px, LineStyle.Solid, Colors.LightGray)
                        .color(Theme.DarkGreen.rgb)
                        .toAttrs()
                ) {
                    Text("Trusted by ")
                    B { Text("400+ facilities") }
                    Text(" across ")
                    B { Text("26 states") }
                    Text(" — ")
                    B { Text("\$55M+") }
                    Text(" in rebates secured.")
                }

                // Socials Links
                Div(
                    attrs = Modifier
                        .classNames("d-flex", "justify-content-start", "align-items-center", "gap-4", "mt-3")
                        .toAttrs()
                )
                {
                    Link(
                        path = "https://www.facebook.com/greenrebates/",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our Facebook page")
                            }
                    ) {
                        FaFacebook(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://www.linkedin.com/company/green-rebates",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our LinkedIn page")
                            }
                    ) {
                        FaLinkedin(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://www.instagram.com/greenrebates/",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our Instagram page")
                            }
                    ) {
                        FaInstagram(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://www.tiktok.com/@green.rebates",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our Tiktok page")
                            }
                    ) {
                        FaTiktok(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://www.youtube.com/channel/UCjREfEKvNsrM0wyNN_aQaEw",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "See more on our YouTube channel")
                            }
                    ) {
                        FaYoutube(
                            size = IconSize.LG
                        )
                    }

                    Link(
                        path = "https://x.com/greenrebates1",
                        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = SocialsIconStyle.toModifier()
                            .attrsModifier {
                                attr("aria-label", "Read more on our X/Twitter page")
                            }
                    ) {
                        FaXTwitter(
                            size = IconSize.LG
                        )
                    }
                }

                // Mobile only: iFrame below trust line
                Div(
                    attrs = Modifier
                        .classNames("d-lg-none")
                        .fillMaxWidth()
                        .margin(top = 24.px)
                        .toAttrs()
                ) {
                    Section(
                        attrs = Modifier
                            .classNames("ratio", "ratio-16x9", "mx-auto")
                            .fillMaxWidth()
                            .toAttrs()
                    ) {
                        Iframe(
                            attrs = Modifier.toAttrs {
                                attr("loading", "lazy")
                                attr("src", "https://www.youtube-nocookie.com/embed/866Mh94VtBc?si=J_lgbx_SAuxgCppd")
                                attr("title", "YouTube video player")
                                attr("frameborder", "0")
                                attr("referrerpolicy", "strict-origin-when-cross-origin")
                                attr("allowfullscreen", "true")
                            }
                        )
                    }
                }
            }
        }

        // Stats Section
        Section(
            attrs = Modifier
                .backgroundColor(Theme.MediumGreen.rgb)
                .classNames("gx-0", "py-5", "py-lg-8", "w-100")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {           // ← regular container = capped width
                Div(
                    attrs = Modifier
                        .color(Theme.DarkGreen.rgb)
                        .classNames(
                            "row", "row-cols-1", "row-cols-lg-4",
                            "g-4", "g-lg-5", "justify-content-center"
                        )
                        .toAttrs()
                ) {

                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "justify-content-lg-center").toAttrs()) {
                        // Your content here (card, icon, text, etc.)
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            H3(Modifier.classNames("fs-1").toAttrs()) { Text("$55 Million") }
                            P { Text("Energy Rebates Approved") }
                        }
                    }

                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "justify-content-lg-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            H3(Modifier.classNames("fs-1").toAttrs()) { Text("400+") }
                            P { Text("Completed Projects") }
                        }
                    }

                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "justify-content-lg-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            H3(Modifier.classNames("fs-1").toAttrs()) { Text("26") }
                            P { Text("States/Provinces") }
                        }
                    }

                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "justify-content-lg-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            H3(Modifier.classNames("fs-1").toAttrs()) { Text("420M") }
                            P { Text("kWh saved and counting") }
                        }
                    }
                }
            }
        }

        // Why Us Section
        Section(
            attrs = Modifier
                .backgroundColor(Theme.DarkGreen.rgb)
                .classNames("gx-0", "py-5", "py-lg-8", "w-100")
                .toAttrs()
        )
        {
            Div(
                attrs = Modifier
                    .classNames("container", "w-100")
                    .toAttrs()
            ) {

                H2(
                    attrs = Modifier
                        .color(Theme.MediumGreen.rgb)
                        .classNames("fs-1", "fw-bold", "mx-auto", "w-100", "text-center", "mb-5")
                        .toAttrs()
                ) {
                    Text("WHY WORK WITH US?")
                }

                Div(
                    attrs = Modifier
                        .color(Colors.White)
                        .classNames(
                            "row", "row-cols-1", "row-cols-lg-3",
                            "g-4", "g-lg-5", "justify-content-center"
                        )
                        .toAttrs()
                ) {

                    Div(attrs = Modifier.classNames("col").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            Img(
                                src = "discount.webp",
                                alt = "Discount Sign",
                                attrs = Modifier
                                    .width(75.px)
                                    .classNames("mx-auto", "mb-3")
                                    .toAttrs {
                                        attr("loading", "lazy")
                                    }
                            )
                            H3(Modifier.classNames("fs-3", "mb-2").toAttrs()) { Text("Huge Savings") }
                            P { Text("Rebates can cover up to 100% of your project. We make sure you get the most.") }
                        }
                    }

                    Div(attrs = Modifier.classNames("col").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            Img(
                                src = "puzzle-piece.webp",
                                alt = "Puzzle Piece Sign",
                                attrs = Modifier
                                    .width(75.px)
                                    .classNames("mx-auto", "mb-3")
                                    .toAttrs {
                                        attr("loading", "lazy")
                                    }
                            )
                            H3(Modifier.classNames("fs-3", "mb-2").toAttrs()) { Text("Rebates Made Easy") }
                            P { Text("We handle the complexity so you don’t have to. Simple, fast, done right.") }
                        }
                    }

                    Div(attrs = Modifier.classNames("col").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            Img(
                                src = "briefcase-4.webp",
                                alt = "Puzzle Piece Sign",
                                attrs = Modifier
                                    .width(75.px)
                                    .classNames("mx-auto", "mb-3")
                                    .toAttrs {
                                        attr("loading", "lazy")
                                    }
                            )
                            H3(Modifier.classNames("fs-3", "mb-2").toAttrs()) { Text("Horticulture Experts") }
                            P { Text("Built for growers. We know the space and get bigger rebates, faster.") }
                        }
                    }
                }
            }
        }

        // About Us Section
        Section(
            attrs = Modifier
                .id("about-sec")
                .classNames("container-fluid", "px-0", "py-5", "my-2", "my-lg-4")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "align-items-center").toAttrs()) {

                    // IMAGE — left on desktop, full-width on top on mobile
                    Div(attrs = Modifier.classNames("col-lg-6", "order-lg-1", "order-2", "my-3").toAttrs()) {
                        Img(
                            src = "layers.webp",
                            attrs = Modifier
                                .classNames("img-fluid", "rounded-4", "shadow", "w-100")
                                .toAttrs {
                                    attr("loading", "lazy")
                                }
                        )
                    }

                    // TEXT COLUMN — right on desktop, full-width on top on mobile
                    Div(attrs = Modifier.classNames("col-lg-6", "order-lg-2", "order-1", "text-lg-start", "text-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "h-100", "justify-content-center", "gap-4").toAttrs()) {

                            H2(
                                attrs = Modifier
                                    .color(Theme.DarkGreen.rgb)
                                    .classNames("fw-bold", "mb-2", "text-center")
                                    .toAttrs()
                            ) { Text("ABOUT US") }

                            // Paragraph
                            P(attrs = Modifier.color(Theme.DarkGreen.rgb).classNames("mb-3").toAttrs()) {
                                Text("Green Rebates is a leading energy incentive management firm serving the horticulture industry. We connect commercial indoor growers with utility programs to secure cash incentives for energy-efficient upgrades. Our clients have earned millions through high-efficiency LED lighting, advanced HVAC systems, and industrial dehumidification. ")

                                Link(
                                    path = "/meet-the-team",
                                    text = "Meet the experts behind your rebates.",
                                    variant = UncoloredLinkVariant,
                                    modifier = Modifier.color(Theme.MediumGreen.rgb).textDecorationLine(TextDecorationLine.Underline),
                                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                                )
                            }

                            Div(
                                attrs = HeroCtaRowStyle.toModifier()
                                    .classNames("d-flex", "flex-wrap", "flex-row")
                                    .toAttrs()
                            ) {

                                Link(
                                    path = "/contact-us",
                                    text = "GET REBATES NOW",
                                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                    modifier = ButtonHoverDarkGreen.toModifier()
                                        .classNames("fs-6", "px-4", "py-3", "shadow")
                                        .role("button").attrsModifier {
                                            attr("aria-label", "To Contact Us Page")
                                        },
                                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                                )

                                Link(
                                    path = "/our-services",
                                    text = "OUR SERVICES",
                                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                    modifier = ButtonHoverDarkGreen.toModifier()
                                        .classNames("fs-6", "px-4", "py-3")
                                        .role("button").attrsModifier {
                                            attr("aria-label", "See how Green Rebates works")
                                        },
                                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                                )
                            }

                        }
                    }
                }
            }
        }

        // Grower's Profile
        Section(
            attrs = Modifier
                .id("testimonial-sec")
                .classNames("gx-0", "mt-2", "mb-3","w-100")
                .backgroundColor(Colors.White)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {

                Div(attrs = GrowersProfileContainer.toModifier().classNames("row", "align-items-center", "py-4").toAttrs()) {

                    // TEXT COLUMN — right on desktop, full-width on top on mobile
                    Div(attrs = Modifier.classNames("col-lg-6", "order-lg-1", "order-1", "text-lg-start", "text-center", "py-2").toAttrs()) {
                        Div(attrs = Modifier.classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "h-100", "justify-content-start").toAttrs()) {

                            H3(
                                attrs = Modifier
                                    .color(Theme.DarkGreen.rgb)
                                    .classNames("mb-2")
                                    .toAttrs()
                            ) {
                                Text("GROWER SPOTLIGHT")
//                                SpanText(
//                                    text = "LIGHT",
//                                    modifier = GreenGradientTextStyle.toModifier()
//                                )
                            }

                            H2(
                                attrs = LandingAttributionStyle.toModifier()
                                    .color(Colors.WhiteSmoke).letterSpacing(1.px)
                                    .classNames("mb-3", "fs-2")
                                    .toAttrs()
                            ) {
                                Text("REAL MCCOY FARMS")
                            }

                            P(
                                attrs = LandingBodyStyle.toModifier()
                                    .fontStyle(FontStyle.Italic)
                                    .color(Theme.DarkGreen.rgb)
                                    .toAttrs()
                            ) {
                                Span(attrs = LandingQuoteMarkStyle.toModifier()
                                    .fontStyle(FontStyle.Normal)
                                    .color(Theme.DarkGreen.rgb)
                                    .opacity(75.percent)
                                    .toAttrs()
                                ) {
                                    Text("“")
                                }

                                Text("We got a $370,000 rebate from Green Rebates. And I think we're going to get a much larger one for this new project in Missouri. " +
                                        "I just had no idea it would be that much. And the power company was stoked to give it out!")
                            }

                            P(
                                attrs = LandingAttributionStyle.toModifier()
                                    .fontStyle(FontStyle.Italic).fontWeight(FontWeight.Normal)
                                    .fillMaxWidth().color(Theme.DarkGreen.rgb)
                                    .textAlign(TextAlign.Right)
                                    .toAttrs()
                            ) { Text("- Mike McCoy") }

                            Link(
                                path = "https://www.instagram.com/the_realmccoyfarms/",
                                variant = UncoloredLinkVariant,
                                modifier = Modifier.color(Theme.DarkGreen.rgb).fontSize(1.cssRem).opacity(0.75),
                                openInternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
                                openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB
                            ) {
                                FaInstagram(Modifier.margin(right = 4.px))
                                Text("Visit their Instagram →",)
                            }

                            // Trust line
                            Div(
                                attrs = Modifier
                                    .classNames("mt-3", "d-flex", "flex-column", "flex-md-row", "gap-3")
                                    .color(Theme.DarkGreen.rgb)
                                    .toAttrs()
                            ) {
                                listOf(
                                    "\$127K" to "utility rebates secured",
                                    "1,860" to "LED fixtures upgraded",
                                    "34%" to "estimated annual energy reduction"
                                ).forEach { (stat, label) ->
                                    Div(attrs = Modifier.classNames("d-flex", "flex-column").toAttrs()) {
                                        B(attrs = Modifier.fontSize(1.5.cssRem).toAttrs()) { Text(stat) }
                                        Span(attrs = Modifier.fontSize(0.875.cssRem).toAttrs()) { Text(label) }
                                    }
                                }
                            }
                        }
                    }

                    // IMAGE — left on desktop, full-width on top on mobile
                    Div(attrs = Modifier.classNames("col-lg-6", "order-lg-2", "order-2").toAttrs()) {
                        Img(
                            src = "images/growerprofile_mccoy.png",
                            alt = "Grow facility example",
                            attrs = Modifier
                                .classNames("img-fluid", "rounded-4", "shadow", "w-100")
                                .toAttrs {
                                    attr("loading", "lazy")
                                }
                        )
                    }
                }
            }
        }

        // Testimonials
        Section(
            attrs = Modifier
                .id("testimonial-sec")
                .classNames("gx-0", "py-5", "my-3","py-lg-8", "w-100")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container-lg").toAttrs()) {
                H2(
                    attrs = Modifier
                        .color(Theme.DarkGreen.rgb)
                        .classNames("fw-bold", "mb-3", "text-center")
                        .toAttrs()
                ) { Text("What Our Clients Say") }

                Div(
                    attrs = Modifier
                        .classNames("d-flex", "flex-nowrap", "gap-5", "overflow-auto", "py-5", "align-items-stretch")
                        .toAttrs()
                ) {
                    listOf(
                        "I had no idea I could get money back for LED grow lights. I had already decided to buy the lights, then Green Rebates ended up getting me a \$175,000 check. It was like hitting the jackpot in Vegas! I couldn’t believe it." to "PORTLAND, OR – URBAN AGRICULTURE",
                        "Green Rebates has been a game-changer for our cultivation facility. Their knowledge and expertise in energy rebates, specifically tailored to the needs of larger grow operations, have been invaluable." to "INDICOOL INDUSTRIES",
                        "I was on the fence about switching to LED grow lights. But when I started working with Green Rebates and they told me how much I would be saving I couldn’t say no. I wouldn’t work with anyone else." to "OKLAHOMA CITY, OK – INDOOR AGRICULTURE",
                        "As a cultivator, finding cost-effective solutions for energy efficiency is crucial for our business. That’s why we turned to Green Rebates, and they exceeded our expectations. Their expertise in energy-saving technologies for larger grow facilities is unmatched." to "TERPINATOR FARMS"
                    ).forEach { (quote, attribution) ->
                        Div(attrs = HomeScrollCardItemStyle.toModifier().toAttrs()) {
                            Div(attrs = HomeTestimonialCardStyle.toModifier().toAttrs()) {
                                Div(attrs = LandingQuoteMarkStyle.toModifier().classNames("mb-2").toAttrs()) {
                                    Text("“")
                                }
                                P(
                                    attrs = LandingBodyStyle.toModifier()
                                        .fontStyle(FontStyle.Italic)
                                        .color(Theme.DarkGreen.rgb)
                                        .opacity(0.85)
                                        .toAttrs()
                                ) { Text(quote) }
                                P(
                                    attrs = LandingAttributionStyle.toModifier()
                                        .margin(top = 1.cssRem, bottom = 0.px)
                                        .toAttrs()
                                ) { Text(attribution) }
                            }
                        }
                    }
                }
            }
        }


        // Energy Partners Section - Idea #1 Compact with highlighting top partners
        Section(
            attrs = Modifier
                .id("partners-sec")
                .classNames("gx-0", "py-5", "w-100", "d-flex", "flex-column", "justify-content-center")
                .backgroundColor(Colors.White)
                .toAttrs()
        )
        {
            H2(
                attrs = Modifier
                    .classNames("fw-bold", "mb-2", "text-center")
                    .toAttrs()
            ) {
                Link(
                    path = "/partners",
                    text = "PARTNERS",
                    variant = UncoloredLinkVariant,
                    modifier = Modifier.color(Theme.DarkGreen.rgb),
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }

            PartnersSectionAnimated()
        }

        // Newest Blogs
        Section(
            attrs = Modifier
                .classNames("gx-0", "py-5", "py-lg-8", "w-100")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container-lg").toAttrs()) {
                Div(attrs = Modifier.classNames("d-flex", "justify-content-between", "align-items-baseline", "mb-4").toAttrs()) {
                    H2(attrs = Modifier.color(Theme.DarkGreen.rgb).classNames("fw-bold", "mb-0").toAttrs()) {
                        Text("Newest Blogs")
                    }
                    Link(
                        path = SitePage.Blog.route,
                        text = "See All →",
                        variant = UncoloredLinkVariant,
                        modifier = Modifier.color(Theme.DarkGreen.rgb).fontSize(1.cssRem),
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }
                Div(attrs = Modifier.classNames("d-flex", "flex-nowrap", "gap-3", "overflow-auto", "py-2", "align-items-stretch").toAttrs()) {
                    BLOG_ENTRIES.take(4).forEach { entry ->
                        Div(attrs = HomeScrollCardItemStyle.toModifier().toAttrs()) {
                            BlogPreviewCard(entry, showShadow = false)
                        }
                    }
                }
            }
        }

        // Podcasts
        Section(
            attrs = Modifier
                .classNames("gx-0", "py-5", "py-lg-8", "w-100")
                .backgroundColor(Colors.White)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container-lg").toAttrs()) {
                Div(attrs = Modifier.classNames("d-flex", "justify-content-between", "align-items-baseline", "mb-4").toAttrs()) {
                    H2(attrs = Modifier.color(Theme.DarkGreen.rgb).classNames("fw-bold", "mb-0").toAttrs()) {
                        Text("Podcasts")
                    }
                    Link(
                        path = SitePage.Podcast.route,
                        text = "See All →",
                        variant = UncoloredLinkVariant,
                        modifier = Modifier.color(Theme.DarkGreen.rgb).fontSize(1.cssRem),
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }
                Div(attrs = Modifier.classNames("d-flex", "flex-nowrap", "gap-3", "overflow-auto", "py-2", "align-items-stretch").toAttrs()) {
                    PODCAST_LIST.getPopular().take(4).forEach { entry ->
                        Div(attrs = HomeScrollCardItemStyle.toModifier().toAttrs()) {
                            PodcastPreviewCard(entry, showShadow = false)
                        }
                    }
                }
            }
        }

        // Articles
        Section(
            attrs = Modifier
                .classNames("gx-0", "py-5", "py-lg-8", "w-100")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container-lg").toAttrs()) {
                Div(attrs = Modifier.classNames("d-flex", "justify-content-between", "align-items-baseline", "mb-4").toAttrs()) {
                    H2(attrs = Modifier.color(Theme.DarkGreen.rgb).classNames("fw-bold", "mb-0").toAttrs()) {
                        Text("Latest Articles")
                    }
                    Link(
                        path = SitePage.Article.route,
                        text = "See All →",
                        variant = UncoloredLinkVariant,
                        modifier = Modifier.color(Theme.DarkGreen.rgb).fontSize(1.cssRem),
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }
                Div(attrs = Modifier.classNames("d-flex", "flex-nowrap", "gap-3", "overflow-auto", "py-2", "align-items-stretch").toAttrs()) {
                    ARTICLE_ENTRIES.take(4).forEach { entry ->
                        Div(attrs = HomeScrollCardItemStyle.toModifier().toAttrs()) {
                            ArticlePreviewCard(entry, showShadow = false)
                        }
                    }
                }
            }
        }

        // Next Events
        Section(
            attrs = Modifier
                .classNames("gx-0", "py-5", "py-lg-8", "w-100")
                .backgroundColor(Colors.White)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container-lg").toAttrs()) {
                Div(attrs = Modifier.classNames("d-flex", "justify-content-between", "align-items-baseline", "mb-4").toAttrs()) {
                    H2(attrs = Modifier.color(Theme.DarkGreen.rgb).classNames("fw-bold", "mb-0").toAttrs()) {
                        Text("Next Events")
                    }
                    Link(
                        path = SitePage.ContactUs.route,
                        text = "Get in Touch →",
                        variant = UncoloredLinkVariant,
                        modifier = Modifier.color(Theme.DarkGreen.rgb).fontSize(1.cssRem),
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }
                Div(attrs = Modifier.classNames("d-flex", "flex-nowrap", "gap-3", "overflow-auto", "py-2", "align-items-stretch").toAttrs()) {
                    NEXT_EVENTS.forEach { event ->
                        Div(attrs = HomeScrollCardItemStyle.toModifier().toAttrs()) {
                            NextEventCard(event)
                        }
                    }
                }
            }
        }

        // FAQ
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-2", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "py-5").toAttrs()) {
                P(
                    attrs = Modifier
                        .classNames("text-center", "fw-bold", "mb-2")
                        .color(Theme.MediumGreen.rgb)
                        .fontSize(0.7.cssRem)
                        .letterSpacing(0.15.em)
                        .textTransform(TextTransform.Uppercase)
                        .toAttrs()
                ) { Text("COMMONLY ASKED QUESTIONS") }
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) { Text("Everything You Need to Know") }
                FaqAccordion(
                    accordionId = "home-faq",
                    items = listOf(
                        FaqItem(
                            question = "What types of rebates does Green Rebates help with?",
                            answer = "We specialize in energy rebates and incentives for LED grow lighting, HVAC systems, industrial dehumidifiers, and other energy-efficient equipment upgrades for commercial cultivation facilities."
                        ),
                        FaqItem(
                            question = "How much money can I get back?",
                            answer = "Rebate amounts vary by utility, equipment type, and project size. Our clients typically receive anywhere from a few thousand dollars to over \$200,000 per project — and we've helped facilities collectively secure millions in total incentives."
                        ),
                        FaqItem(
                            question = "What does it cost to get started?",
                            answer = "Green Rebates works on a success-based model — we only get paid when you do. There are no upfront costs or fees to begin the process."
                        ),
                        FaqItem(
                            question = "What states do you currently work in?",
                            answer = "We currently work across 26 states and provinces. Our network of utility partnerships covers major cannabis and horticulture markets nationwide. Visit our States page to see if your state is covered."
                        ),
                        FaqItem(
                            question = "How long does the rebate process take?",
                            answer = "Timelines vary by utility program, but most rebates are approved and disbursed within 90–120 days of project completion. We handle the paperwork and follow up every step of the way."
                        ),
                        FaqItem(
                            question = "Does my equipment need to be DLC qualified?",
                            answer = "Many utility programs require equipment to be on the DesignLights Consortium (DLC) qualified products list. Our team verifies your equipment qualifies and can recommend DLC-approved alternatives if needed."
                        )
                    )
                )
            }
        }

        // Contact Us
        Section(
            attrs = Modifier
                .id("contact-sec")
                .backgroundColor(Theme.DarkGreen.rgb)
                .classNames("gx-0", "pt-2", "pb-5", "w-100", "d-flex", "flex-column", "justify-content-center")
                .toAttrs()
        )
        {
            H3(
                attrs = Modifier
                    .color(Theme.MediumGreen.rgb)
                    .classNames("fs-1", "fw-bold", "w-100", "text-center", "mb-3", "pt-lg-5")
                    .toAttrs()
            ) {
                Text("CONTACT US FOR A FREE ESTIMATE")
            }

            Link(
                path = "/contact-us",
                text = "GET REBATES NOW",
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                modifier = ButtonHoverWhite.toModifier()
                    .classNames("fs-5", "mt-3", "px-4", "py-3", "mx-auto", "mb-lg-5")
                    .role("button").attrsModifier {
                        attr("aria-label", "To Contact Us Page")
                    },
                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
            )

        }

    }
}